import React, { useState } from "react";
import Modal from "react-modal";
import { Button } from "../../Button";
import { TextField } from "../../TextField";
import PermissionsCheckboxGroup from "../PermissionsCheckboxGroup";
import { AdminPermission } from "../../../api/generated/models/AdminPermission";
import { AdminAccountsService } from "../../../api/generated/services/AdminAccountsService";
import { useAuth } from "../../../context/AuthContext";
import { ReactComponent as CloseIcon } from "../../../assets/icons/x.svg";

interface AddAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddAdminModal: React.FC<AddAdminModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState<
    AdminPermission[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (!loading) {
      setEmail("");
      setSelectedPermissions([]);
      setError(null);
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email) {
      setError("Email is required");
      return;
    }

    if (selectedPermissions.length === 0) {
      setError("Please select at least one permission");
      return;
    }

    setLoading(true);

    try {
      await AdminAccountsService.postApiAdminAccountsCreateAdminAccount({
        email,
        permissions: selectedPermissions,
      });
      handleClose();
      onSuccess();
    } catch (err: any) {
      console.error("Error creating admin account:", err);
      setError(
        err?.body?.message ||
          err?.message ||
          "Failed to create admin account. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      className="modal-content md:max-w-[600px]"
      overlayClassName="modal-overlay"
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel="Add Admin"
    >
      <div className="flex justify-between mb-6">
        <h2 className="text-primary font-medium text-xl">Add Admin</h2>
        <Button onClick={handleClose} variant="icon" disabled={loading}>
          <CloseIcon className="w-3 h-3" />
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <TextField
          type="email"
          id="email"
          label="Email Address"
          name="email"
          value={email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
          required
          disabled={loading}
        />

        <PermissionsCheckboxGroup
          selectedPermissions={selectedPermissions}
          onChange={setSelectedPermissions}
          grantorPermissions={user?.adminPermissions ?? []}
          disabled={loading}
        />

        {error && (
          <div className="text-red-500 text-sm bg-red-50 border border-red-200 p-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="flex gap-3 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button type="submit" isLoading={loading}>
            Add Admin
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AddAdminModal;
